/**
 * Mix maths for the two big continuous voices: the aircraft and the wind. Pure functions of sim
 * state, written into caller-owned objects so the per-frame path allocates nothing.
 *
 * The Osprey is the exception to distance attenuation: from the moment it is inbound it is
 * audible ANYWHERE on the map (01 §10), first as a low thrum from the south-east that grows
 * for 45 seconds, then as the loudest thing in the game over the pickup point.
 */

import { INSERTION, OSPREY } from '../sim/tuning';
import type { Biome, MissionPhase, OspreyPhase, WeatherKind } from '../sim/types';
import { type Spatial, spatialise } from './spatial';

export interface AircraftMix {
  gain: number;
  cutoff: number;
  /** Engine and rotor speed, 1 = hover. */
  rate: number;
  pan: number;
}

export const createAircraftMix = (): AircraftMix => ({ gain: 0, cutoff: 800, rate: 1, pan: 0 });

export interface AircraftInput {
  phase: MissionPhase;
  altitude: number;
  osprey: OspreyPhase;
  ospreyTicks: number;
  recalled: boolean;
  /** Seconds since the `departed` phase began. */
  departedSeconds: number;
  pickupX: number;
  pickupZ: number;
  listenerX: number;
  listenerZ: number;
}

/** How far out the inbound leg starts, metres, and the unit vector it comes in along (from the SE). */
const INBOUND_DISTANCE = 160;
const FROM_X = 0.543;
const FROM_Z = 0.84;
const AIRCRAFT_RANGE = 400;
const AIRCRAFT_SCALE = 0.4;
const HOVER_FLOOR = 0.3;
const CABIN = { gain: 0.5, cutoff: 1500 } as const;

export function aircraftMix(i: AircraftInput, scratch: Spatial, out: AircraftMix): AircraftMix {
  out.pan = 0;
  out.rate = 1;
  if (i.phase !== 'ground') {
    // Aboard, the engines are all there is; after the jump they fall away within two seconds
    // and the rest of the descent is wind only (07 §4).
    const fallen = (INSERTION.altitude - i.altitude) / (INSERTION.altitude * 0.25);
    const aboard = i.phase === 'flyover' ? 1 : Math.max(0, Math.min(1, 1 - fallen));
    out.gain = CABIN.gain * aboard;
    out.cutoff = 600 + (CABIN.cutoff - 600) * aboard;
    return out;
  }
  if (i.osprey === 'away') {
    out.gain = 0;
    out.cutoff = 500;
    return out;
  }
  let ax = i.pickupX;
  let az = i.pickupZ - 7;
  let floor = HOVER_FLOOR;
  let fade = 1;
  if (i.osprey === 'inbound') {
    const t = i.recalled ? 1 - Math.min(1, Math.max(0, i.ospreyTicks / OSPREY.inboundTicks)) : 1;
    const d = INBOUND_DISTANCE * (1 - t);
    ax += FROM_X * d;
    az += FROM_Z * d;
    floor = 0.1 + (HOVER_FLOOR - 0.1) * t;
    out.rate = 0.94 + 0.06 * t;
  } else if (i.osprey === 'departed') {
    const s = i.departedSeconds;
    ax -= 6 * s * s * 0.24;
    az -= 6 * s * s * 0.97;
    fade = Math.max(0, 1 - s / 7);
    out.rate = 1.06;
  }
  // A big source: distances are scaled so it is at full level anywhere within 15 m, and pans slowly.
  const k = AIRCRAFT_SCALE;
  spatialise((ax - i.listenerX) * k, (az - i.listenerZ) * k, AIRCRAFT_RANGE * k, false, scratch);
  out.gain = Math.max(floor, scratch.gain) * fade;
  out.cutoff = Math.max(450, Math.min(7000, scratch.cutoff * 0.45));
  out.pan = scratch.pan;
  return out;
}

/* --------------------------------------------------------------------------- wind */

export interface WindMix {
  gain: number;
  cutoff: number;
  q: number;
  /** A thin high whistle over the top: blizzards and sandstorms only. */
  whistle: number;
  /** Steady rain hiss. */
  rain: number;
}

export const createWindMix = (): WindMix => ({ gain: 0, cutoff: 600, q: 1, whistle: 0, rain: 0 });

const BIOME_WIND: Record<Biome, { gain: number; cutoff: number; q: number }> = {
  arctic: { gain: 0.12, cutoff: 420, q: 1.3 },
  desert: { gain: 0.1, cutoff: 1100, q: 0.6 },
  temperate: { gain: 0.06, cutoff: 650, q: 0.9 },
};
/**
 * ROUND5: `thunderstorm` blows nearly as hard as the whiteout and the brown-out - the Storm card
 * sells one "heavy wind" for every biome - but it is a squall over woodland rather than driven ice
 * or sand, so the rain hiss carries it and the whistle stays with the two that earn it.
 */
const WEATHER_WIND: Record<WeatherKind, number> = {
  clear: 1, snow: 1.25, rain: 1.15, blizzard: 2.4, sandstorm: 2.4, thunderstorm: 2,
};
/** Steady hiss under each of the two wet skies: a thunderstorm is the heavier rain, and sounds it. */
const RAIN_HISS: Partial<Record<WeatherKind, number>> = { rain: 0.06, thunderstorm: 0.11 };
/** Rushing air under the canopy at jump height. */
const CANOPY_WIND = 0.5;

/** A slow, never-repeating-in-practice gust curve, -1..1, from a running clock in seconds. */
export function gustAt(t: number): number {
  return 0.6 * Math.sin(0.31 * t) + 0.3 * Math.sin(0.83 * t + 1.3) + 0.1 * Math.sin(2.1 * t + 4);
}

export function windMix(
  biome: Biome,
  weather: WeatherKind,
  phase: MissionPhase,
  altitude: number,
  gust: number,
  out: WindMix,
): WindMix {
  const b = BIOME_WIND[biome];
  // The whistle is the voice of driven ice and sand over open ground, not of every storm: a
  // thunderstorm is loud in the rain, not thin over the top of it.
  const driven = weather === 'blizzard' || weather === 'sandstorm';
  let gain = b.gain * WEATHER_WIND[weather];
  if (phase === 'flyover') gain *= 0.3;
  else if (phase === 'descent') gain += (CANOPY_WIND - gain) * Math.min(1, altitude / INSERTION.altitude);
  out.gain = gain * (0.75 + 0.25 * gust);
  out.cutoff = b.cutoff * (1 + 0.35 * gust) * (phase === 'descent' ? 1.5 : 1);
  out.q = b.q;
  out.whistle = driven ? 0.035 * (0.6 + 0.4 * gust) : 0;
  out.rain = phase === 'flyover' ? 0 : RAIN_HISS[weather] ?? 0;
  return out;
}
